import type { LstMotivoParada, LstStatus } from '../types';

/** Símbolos Kamishibai digital (O · Δ · X) + exceção Processo Parado. */
export type KamishibaiSymbol = 'O' | 'triangle' | 'X';
export type LstDisplaySymbol = KamishibaiSymbol | 'stopped' | 'pending' | 'na';

export interface KamishibaiSymbolMeta {
  symbol: KamishibaiSymbol;
  label: string;
  glyph: string;
}

export const KAMISHIBAI_SYMBOLS: Record<KamishibaiSymbol, KamishibaiSymbolMeta> = {
  O: { symbol: 'O', label: 'Conforme', glyph: 'O' },
  triangle: { symbol: 'triangle', label: 'Desvio', glyph: 'Δ' },
  X: { symbol: 'X', label: 'Não realizado', glyph: 'X' },
};

export const LST_MOTIVO_PARADA_LABELS: Record<LstMotivoParada, string> = {
  falta_op: 'Falta de OP',
  setup: 'Setup',
  quebra: 'Quebra',
  falta_material: 'Falta de Material',
};

export const LST_MOTIVO_PARADA_OPTIONS: LstMotivoParada[] = [
  'falta_op',
  'setup',
  'quebra',
  'falta_material',
];

export function encodeMotivoParada(motivo: LstMotivoParada): string {
  return `[PARADA:${motivo}] ${LST_MOTIVO_PARADA_LABELS[motivo]}`;
}

export function decodeMotivoParada(justificativa: string | null | undefined): LstMotivoParada | null {
  if (!justificativa) return null;
  const match = justificativa.match(/\[PARADA:([a-z_]+)\]/);
  if (!match) return null;
  const key = match[1] as LstMotivoParada;
  return LST_MOTIVO_PARADA_OPTIONS.includes(key) ? key : null;
}

/** Texto curto de desvio para exibição na listagem (sem metadados MASP/parada). */
export function formatDesvioJustificativa(justificativa: string | null | undefined): string | null {
  if (!justificativa) return null;
  const trimmed = justificativa
    .replace(/^\[MASP #[^\]]+\]\s*/i, '')
    .replace(/^\[PARADA:[^\]]+\]\s*/i, '')
    .trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Conta positivamente na Disciplina do TPL (O, Δ, Processo Parado). */
export function isLstDisciplineExecuted(status: LstStatus | null | undefined): boolean {
  if (!status) return false;
  return status === 'realizado' || status === 'realizado_com_desvio' || status === 'processo_parado';
}

/** Entra no denominador da Conformidade do Processo (apenas O e Δ). */
export function countsForConformityDenominator(status: LstStatus | null | undefined): boolean {
  return status === 'realizado' || status === 'realizado_com_desvio';
}

export function lstStatusToDisplaySymbol(
  status: LstStatus | 'pendente' | null | undefined,
): LstDisplaySymbol | null {
  if (!status || status === 'pendente') return status === 'pendente' ? 'pending' : null;
  if (status === 'realizado') return 'O';
  if (status === 'realizado_com_desvio') return 'triangle';
  if (status === 'processo_parado') return 'stopped';
  return 'X';
}

export function lstStatusToKamishibai(status: LstStatus | null | undefined): KamishibaiSymbol | null {
  const sym = lstStatusToDisplaySymbol(status);
  if (sym === 'O' || sym === 'triangle' || sym === 'X') return sym;
  return null;
}

export function kamishibaiToLstStatus(symbol: KamishibaiSymbol): LstStatus {
  if (symbol === 'O') return 'realizado';
  if (symbol === 'triangle') return 'realizado_com_desvio';
  return 'nao_realizado';
}

export const KAMISHIBAI_SYMBOL_STYLES: Record<
  KamishibaiSymbol,
  { bg: string; text: string; ring: string; button: string; buttonActive: string }
> = {
  O: {
    bg: 'bg-emerald-50 dark:bg-emerald-950/40',
    text: 'text-emerald-700 dark:text-emerald-300',
    ring: 'ring-emerald-500/40',
    button:
      'border-emerald-300 bg-white hover:bg-emerald-50 dark:border-emerald-700 dark:bg-slate-900 dark:hover:bg-emerald-950/30',
    buttonActive: 'border-emerald-600 bg-emerald-500 text-white shadow-lg shadow-emerald-500/30',
  },
  triangle: {
    bg: 'bg-amber-50 dark:bg-amber-950/40',
    text: 'text-amber-700 dark:text-amber-300',
    ring: 'ring-amber-500/40',
    button:
      'border-amber-300 bg-white hover:bg-amber-50 dark:border-amber-700 dark:bg-slate-900 dark:hover:bg-amber-950/30',
    buttonActive: 'border-amber-600 bg-amber-500 text-white shadow-lg shadow-amber-500/30',
  },
  X: {
    bg: 'bg-red-50 dark:bg-red-950/40',
    text: 'text-red-700 dark:text-red-300',
    ring: 'ring-red-500/40',
    button:
      'border-red-300 bg-white hover:bg-red-50 dark:border-red-700 dark:bg-slate-900 dark:hover:bg-red-950/30',
    buttonActive: 'border-red-600 bg-red-500 text-white shadow-lg shadow-red-500/30',
  },
};

export const STOPPED_BADGE_STYLE =
  'bg-slate-200 text-slate-600 ring-slate-400/50 dark:bg-slate-700 dark:text-slate-300 dark:ring-slate-500/40';

/** Estilo de célula N/A — rotina não prevista na data (invisível, só fundo). */
export const KAMISHIBAI_NA_CELL_BG = 'bg-slate-100/90 dark:bg-slate-800/25';

/** @deprecated Use célula vazia com KAMISHIBAI_NA_CELL_BG — mantido para compatibilidade. */
export const KAMISHIBAI_NA_STYLE = {
  dot: 'bg-slate-300 dark:bg-slate-600',
  text: 'text-slate-400 dark:text-slate-500',
};
